import { InputHTMLAttributes, Ref, forwardRef } from 'react';

/**
 * @description Interface extending HTML input attributes for radio input components
 * @interface RadioInputProps
 * @extends {InputHTMLAttributes<HTMLInputElement>}
 */
interface RadioInputProps extends InputHTMLAttributes<HTMLInputElement> {
  /**
   * Additional CSS class name for styling
   */
  classname?: string;
}

/**
 * @description RadioInput component for rendering customizable radio button inputs
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.id] - Unique identifier for the radio input
 * @param {string} [props.title] - Label text to display next to the radio button
 * @param {string} [props.value] - Value of the radio input
 * @param {Function} [props.onChange] - Function to call when radio selection changes
 * @param {boolean} [props.disabled] - Whether the radio input is disabled
 * @param {Function} [props.onBlur] - Function to call when radio input loses focus
 * @param {string} [props.classname] - Additional CSS class for styling the radio button
 * @param {string} [props.name] - Name attribute for the radio input (for grouping)
 * @param {Ref<HTMLInputElement>} ref - Forwarded ref to access the input element
 * @returns {JSX.Element} Rendered radio input component
 */
// eslint-disable-next-line react/display-name
const RadioInput = forwardRef(
  (
    {
      id,
      title,
      value,
      onChange,
      disabled,
      onBlur,
      classname,
      name,
      ...rest
    }: RadioInputProps,
    ref: Ref<HTMLInputElement>,
  ) => {
    return (
      <label
        htmlFor={id}
        className={'radio-button ' + (classname ? classname : '')}
      >
        <input
          id={id}
          type="radio"
          className="form-check-input"
          value={value}
          onChange={onChange}
          disabled={disabled}
          onBlur={onBlur}
          ref={ref}
          name={name}
          {...rest}
        />
        {title && <span>{title}</span>}
      </label>
    );
  },
);

export default RadioInput;
